import {
  InputGroup,
  InputLeftElement,
  Input,
  Box,
  Flex,
  Heading,
  HStack,
  InputProps,
  HeadingProps,
  Spinner,
  IconProps,
  InputRightElement,
  IconButton,
  Button,
} from '@chakra-ui/react';
import { ReactNode, useMemo } from 'react';

import {
  DateRangePickerWithToday,
  DateRangePickerWithTodayPropsType,
} from 'components/DateRangePickerWithToday';
import { FAIcon } from 'components/FAIcon';
import PandaCheck from 'javascript/components/common/PandaCheck';

import { TopBarStyles } from './style';

interface TopBarPropsType {
  title?: string;
  orderType?: string;
  titleProps?: HeadingProps;
  titleIcon?: ReactNode;
  isSearchVisible?: boolean;
  isDateTimeVisible?: boolean;
  elementPlacement?: number[];
  renderElements?: ReactNode[];
  searchFieldProps?: InputProps;
  dateRangeProps?: DateRangePickerWithTodayPropsType;
  loadingProps?: {
    loading: boolean;
  };
  searchIconProps?: IconProps;
  loading?: boolean;
  isSyncedOrderVisible?: boolean;
  isOnlyDeliveryOrderVisible?: boolean;

  //needed for bookworks exception report
  onlyDeliveryOrders?: boolean;
  setOnlyDeliveryOrders?: (val: boolean) => void;
  includeSyncedOrders?: boolean;
  setIncludeSyncedOrders?: (val: boolean) => void;

  // New props for manual search
  onSearchSubmit?: () => void;
  onSearchClear?: () => void;
  showSearchButton?: boolean;
  searchValue?: string;
}

function sortElementsByPosition(nodeElement: ReactNode[], positions: number[]) {
  // Create an array of objects containing both nodeElement and position
  const combinedArray = nodeElement.map((indvElem, index) => ({
    elem: indvElem,
    position: positions[index],
  }));

  // Sort the combined array based on positions
  combinedArray.sort((a, b) => a.position - b.position);

  // Extract only the nodeElements after sorting
  const sortedNodeElement = combinedArray.map((indvELem) => indvELem.elem)?.filter(Boolean);

  return sortedNodeElement;
}

const TopBar = (props: TopBarPropsType) => {
  const {
    title,
    titleProps = {},
    titleIcon,
    renderElements = [],
    elementPlacement = [],
    searchFieldProps = {},
    dateRangeProps = {},
    isSearchVisible = true,
    isDateTimeVisible = true,
    loadingProps,
    searchIconProps = {},
    loading,
    isSyncedOrderVisible = false,
    isOnlyDeliveryOrderVisible = false,
    onlyDeliveryOrders,
    setOnlyDeliveryOrders,
    includeSyncedOrders,
    setIncludeSyncedOrders,
    onSearchSubmit,
    onSearchClear,
    showSearchButton = true,
    searchValue,
  } = props;

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSearchSubmit) {
      onSearchSubmit();
    }
  };

  const handleClearClick = () => {
    if (onSearchClear) {
      onSearchClear();
    }
  };

  const customChildrenRender = useMemo(() => {
    return sortElementsByPosition(
      [
        isOnlyDeliveryOrderVisible && (
          <PandaCheck
            key="only-delivery-checkbox"
            checked={onlyDeliveryOrders ?? false}
            onChange={() => setOnlyDeliveryOrders?.(!onlyDeliveryOrders)}
          >
            Only Delivery Orders
          </PandaCheck>
        ),
        isSyncedOrderVisible && (
          <PandaCheck
            key="synced-orders-checkbox"
            checked={includeSyncedOrders ?? false}
            onChange={() => setIncludeSyncedOrders?.(!includeSyncedOrders)}
          >
            Include Synced Orders
          </PandaCheck>
        ),
        isSearchVisible && (
          <HStack key="search-group" spacing={2}>
            <Box>
              <InputGroup>
                <InputLeftElement w={8} pointerEvents="none">
                  <FAIcon variant="solid" icon="search" color="gray.300" {...searchIconProps} />
                </InputLeftElement>
                <Input
                  id={searchFieldProps?.placeholder || 'filter-text-box'}
                  variant={'filled'}
                  color={'gray'}
                  fontWeight={600}
                  pr={searchValue ? 10 : 2}
                  pl={8}
                  {...searchFieldProps}
                  onKeyPress={handleKeyPress}
                />
                {searchValue && onSearchClear && (
                  <InputRightElement w={8}>
                    <IconButton
                      aria-label="Clear search"
                      icon={<FAIcon variant="solid" icon="times" />}
                      size="sm"
                      variant="ghost"
                      onClick={handleClearClick}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
            </Box>
            {showSearchButton && onSearchSubmit && (
              <Button onClick={onSearchSubmit} size="md" isDisabled={loading}>
                Search
              </Button>
            )}
          </HStack>
        ),
        ...renderElements,
        isDateTimeVisible && (
          <DateRangePickerWithToday key="date-range-picker" {...dateRangeProps} loading={loading} />
        ),
      ],
      elementPlacement
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSearchVisible,
    isSyncedOrderVisible,
    isOnlyDeliveryOrderVisible,
    searchIconProps,
    renderElements,
    isDateTimeVisible,
    dateRangeProps,
    elementPlacement,
    searchValue,
    showSearchButton,
    loading,
  ]);

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      className="main-top-bar"
      sx={{ padding: '0 24px', minHeight: '70px' }}
      boxShadow="lg"
    >
      <Box>
        <Heading size="md" {...titleProps}>
          <Flex align="center" gap={2}>
            {title}
            {titleIcon}
            {loadingProps?.loading && <Spinner sx={TopBarStyles.SpinnerStyles} size={'sm'} />}
          </Flex>
        </Heading>
      </Box>
      <HStack spacing="10px">{customChildrenRender}</HStack>
    </Flex>
  );
};
export { TopBar };
